<template>
  <b-modal
    content-class="simulation-modal-content-class"
    dialog-class="simulation-modal-dialog-class"
    modal-class="simulation-modal-class"
    body-class="d-flex flex-column justify-content-center"
    ref="AnamInterviewModal"
    hide-footer
    hide-header
    no-close-on-backdrop
    centered
    lazy
    @hide="hideModal"
    size="lg"
  >
    <div class="d-flex flex-column mt-2">
      <div class="text-center">
        <h4 class="wrap-text">{{ simulation.title }}</h4>
      </div>

      <b-alert v-if="callError" class="my-2" show variant="danger">
        <div class="d-flex align-items-center">
          <div class="mr-2">
            <i class="material-icons " style="font-size: 25px">warning</i>
          </div>
          <div>
            Can't start interview without mic access. Please allow microphone permission in browser settings.
          </div>
        </div>
      </b-alert>

      <div class="d-flex w-full h-full mt-2 mb-2 flex-column">
        <!-- playsinline keeps mobile Safari from taking the stream fullscreen -->
        <video id="anam-persona-video" ref="personaVideo" autoplay playsinline class="persona-video"></video>
        <audio id="anam-persona-audio" ref="personaAudio" autoplay></audio>

        <transition name="slide">
          <div class="w-full mx-2 d-flex flex-column justify-content-end transcript-container" v-if="isCalling">
            <div v-for="(item, index) in transcript" :key="index">
              <div class="d-flex mb-1">
                <div class="mr-2">
                  <i v-if="item.role === 'agent'" class="material-icons text-muted">record_voice_over</i>
                  <user-avatar
                    v-else
                    slot="aside"
                    :user="getLoggedInUser"
                    size="sm"
                    :file-url="getLoggedInUser.avatar_url"
                    variant="dark"
                  ></user-avatar>
                </div>
                <div>
                  <b class="text-muted">{{ item.role === 'agent' ? 'Agent' : 'You' }}:</b>
                  <span class="ml-1">{{ item.content }}</span>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div v-if="isCalling" style="font-size: 17px;" class="text-center" :class="{ 'text-danger': callEnding }">
          <strong>
            {{ formatTime(time) }}
          </strong>
        </div>
        <div v-else-if="callEnded" class="text-center">
          Call Ended
        </div>
        <div v-else class="text-center">
          Connecting...
        </div>
        <div class="text-center d-flex justify-content-center mb-2">
          <b-btn class="mt-2" @click="hideModalManual" style="border-radius: 10px;" variant="danger">
            End Interview <i class="ml-2 fas fa-phone-slash"></i>
          </b-btn>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import { createClient, AnamEvent } from '@anam-ai/js-sdk';
import UserAvatar from '@/components/User/UserAvatar.vue';

// A sibling of SimulationModal rather than a branch inside it: Anam streams a
// WebRTC video avatar, sources its transcript from the SDK's message history,
// and -- because Anam has no webhook -- has to tell our backend itself when the
// call is over. Same props/events as SimulationModal, so the two are swappable.
export default {
  name: 'AnamInterviewModal',
  components: { UserAvatar },

  props: {
    showModal: { type: Boolean, default: false },
    simulation: { type: Object, default: () => {} },
    // Scenario assignments register their call through the LMS so the call log
    // is tied to a task. Resolves to { access_token, call_id, provider }.
    registerCall: { type: Function, default: null },
  },

  data() {
    return {
      anamClient: null,
      callId: null,
      time: 0,
      interval: null,
      isCalling: false,
      callEnding: false,
      callEnded: false,
      callError: false,
      transcript: [],
      finalized: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
  },

  methods: {
    ...mapActions('simulations', ['registerSimulation', 'anamSessionEnded']),
    get,

    async startCall() {
      let payload = null;
      try {
        if (this.registerCall) {
          payload = await this.registerCall();
        } else {
          const response = await this.registerSimulation({ simulation_id: this.simulation.id });
          payload = get(response, 'data.data', null);
        }
      } catch (e) {
        payload = null;
      }

      const sessionToken = get(payload, 'access_token', null);
      if (!sessionToken) {
        this.callError = true;
        this.$emit('start-failed');
        return;
      }
      this.callId = get(payload, 'call_id', null);

      try {
        this.anamClient = createClient(sessionToken);
        this.anamClient.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, messages => {
          // The SDK's running history is the transcript we report at the end.
          this.transcript = (messages || []).map(m => ({
            role: m.role === 'persona' || m.role === 'assistant' ? 'agent' : 'user',
            content: m.content || m.message || '',
          }));
        });
        this.anamClient.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
          this.isCalling = true;
          this.startTimer();
        });
        // The SDK reports a refused mic explicitly, so we don't have to infer
        // it from a thrown error the way the Retell modal does.
        this.anamClient.addListener(AnamEvent.MIC_PERMISSION_DENIED, () => {
          this.callError = true;
          this.$emit('start-failed');
        });
        // Fires whether the student ended the call or the persona did through
        // its end_call tool. It is one of three triggers now -- Anam's own
        // completion webhook and the reconciliation command are the others --
        // so a lost ping no longer means a lost interview.
        this.anamClient.addListener(AnamEvent.CONNECTION_CLOSED, this.finalize);

        await this.anamClient.streamToVideoAndAudioElements('anam-persona-video', 'anam-persona-audio');
      } catch (e) {
        this.callError = true;
        this.$emit('start-failed');
      }
    },

    async finalize() {
      // CONNECTION_CLOSED and the manual end button can both land here.
      if (this.finalized) return;
      this.finalized = true;
      this.stopTimer();
      this.isCalling = false;
      this.callEnded = true;

      if (this.callId) {
        try {
          // Trigger only -- the transcript is fetched from Anam server-side.
          await this.anamSessionEnded({ call_id: this.callId });
        } catch (e) {
          // The backend's stale-session sweep is the backstop; never block the
          // student on this ping.
        }
      }
      this.$refs.AnamInterviewModal.hide();
      this.$emit('call-ended');
    },

    endCall() {
      if (this.anamClient) {
        this.anamClient.stopStreaming();
      }
      this.finalize();
    },

    async hideModalManual() {
      const isConfirmed = await this.$bvModal.msgBoxConfirm(`Are you sure you want to end interview?`, {
        title: 'Are you sure?',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });
      if (isConfirmed) {
        this.endCall();
      }
    },

    hideModal() {
      this.transcript = [];
      this.$emit('close');
    },

    startTimer() {
      this.interval = setInterval(() => {
        this.time += 1;
      }, 1000);
    },

    stopTimer() {
      if (this.interval) clearInterval(this.interval);
      this.interval = null;
    },

    formatTime(seconds) {
      // The Anam persona cannot hang up on itself the way a Retell agent can,
      // so this timer is the only automatic end to the call.
      if (seconds > 300) {
        this.callEnded = true;
        setTimeout(() => this.endCall(), 1000);
      }
      if (seconds > 270) {
        this.callEnding = true;
      }
      const time = new Date(seconds * 1000);
      return time
        .toTimeString()
        .split(' ')[0]
        .slice(3, time.length);
    },

    // A closed tab would otherwise wait on the backend's stale-session sweep
    // before grading. keepalive (not sendBeacon) because the endpoint is JWT
    // authenticated and a beacon cannot carry the Authorization header.
    reportOnUnload() {
      if (!this.callId || this.finalized) return;
      const url = `${process.env.VUE_APP_BACKEND_BASE_URL}/api/simulations/call-logs/anam/session-ended/`;
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.$store.getters['auth/getAuthToken']}`,
        },
        body: JSON.stringify({ call_id: this.callId }),
      }).catch(() => {});
    },
  },

  watch: {
    showModal(value) {
      if (value) {
        this.$refs.AnamInterviewModal.show();
        this.callEnded = false;
        this.callEnding = false;
        this.callError = false;
        this.finalized = false;
        this.time = 0;
        this.transcript = [];
        this.startCall();
      }
    },
  },

  mounted() {
    window.addEventListener('beforeunload', this.reportOnUnload);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.reportOnUnload);
    this.stopTimer();
    if (this.anamClient) this.anamClient.stopStreaming();
  },
};
</script>

<style lang="scss" scoped>
.wrap-text {
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
}

.persona-video {
  width: 100%;
  max-height: 420px;
  border-radius: 10px;
  background-color: #181818;
  object-fit: cover;
}

.transcript-container {
  height: 160px;
  overflow-y: hidden;
}

.slide-enter-active,
.slide-leave-active {
  transition-duration: 0.5s;
  transition-timing-function: ease-in-out;
}
.slide-enter-to,
.slide-leave {
  max-height: 160px;
  overflow: hidden;
}
.slide-enter,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}
</style>
